import React, { useEffect, useMemo, useState } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
  FaSearch,
} from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";
import SectionCard from "./SectionCard";
import SectionTitle from "./SectionTitle";

import { getAllSections } from "../../../services/sectionsServices";
import { getAllCategoriesAsso } from "../../../services/categoriesAssoServices";

const SECTIONS_PER_PAGE = 12;

const normalizeText = (value = "") =>
  String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const buildPageNumbers = (currentPage, totalPages) => {
  const pages = [];

  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const start = Math.max(1, currentPage - 2);
  const end = Math.min(totalPages, currentPage + 2);

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  if (!pages.includes(1)) {
    pages.unshift(1);
  }

  if (!pages.includes(totalPages)) {
    pages.push(totalPages);
  }

  return [...new Set(pages)].sort((a, b) => a - b);
};

const Sections = () => {
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);

  const [query, setQuery] = useState("");
  const [filteredCategoryId, setFilteredCategoryId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [query, filteredCategoryId]);

  const fetchData = async () => {
    try {
      const [sectionsData, categoriesData] = await Promise.all([
        getAllSections(),
        getAllCategoriesAsso(),
      ]);

      const activeSections = Array.isArray(sectionsData)
        ? sectionsData.filter((section) => section.is_active !== false)
        : [];

      const activeCategories = Array.isArray(categoriesData)
        ? categoriesData
            .filter((category) => category.is_active !== false)
            .sort((a, b) => {
              const orderA = Number(a.ordre ?? 999);
              const orderB = Number(b.ordre ?? 999);

              if (orderA !== orderB) return orderA - orderB;

              return String(a.label || "").localeCompare(
                String(b.label || ""),
                "fr",
                { sensitivity: "base" }
              );
            })
        : [];

      setSections(activeSections);
      setCategories(activeCategories);
    } catch (error) {
      console.error("Erreur lors de la récupération des associations:", error);
    }
  };

  const categoriesById = useMemo(() => {
    return categories.reduce((acc, category) => {
      acc[String(category.id)] = category;
      return acc;
    }, {});
  }, [categories]);

  const orderedSections = useMemo(() => {
    return [...sections].sort((a, b) =>
      String(a.nom_section || "").localeCompare(
        String(b.nom_section || ""),
        "fr",
        { sensitivity: "base" }
      )
    );
  }, [sections]);

  const filteredSections = useMemo(() => {
    const q = normalizeText(query.trim());

    return orderedSections.filter((section) => {
      const category = categoriesById[String(section.category_id)];
      const categoryLabel = category?.label || "Non catégorisée";

      const searchableText = [
        section.nom_section,
        section.discipline_section,
        section.responsable_section,
        section.adresse_section,
        categoryLabel,
      ]
        .filter(Boolean)
        .join(" ");

      const matchesQuery = q
        ? normalizeText(searchableText).includes(q)
        : true;

      const matchesCategory = filteredCategoryId
        ? String(section.category_id) === String(filteredCategoryId)
        : true;

      return matchesQuery && matchesCategory;
    });
  }, [orderedSections, query, filteredCategoryId, categoriesById]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredSections.length / SECTIONS_PER_PAGE)
  );

  const safeCurrentPage = Math.min(currentPage, totalPages);

  const displayedSections = useMemo(() => {
    const startIndex = (safeCurrentPage - 1) * SECTIONS_PER_PAGE;
    const endIndex = startIndex + SECTIONS_PER_PAGE;

    return filteredSections.slice(startIndex, endIndex);
  }, [filteredSections, safeCurrentPage]);

  const pageNumbers = useMemo(
    () => buildPageNumbers(safeCurrentPage, totalPages),
    [safeCurrentPage, totalPages]
  );

  const goToPage = (page) => {
    const nextPage = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(nextPage);

    const sectionElement = document.getElementById("sections-list");
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section className="bg-slate-50 py-16 dark:bg-slate-950 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow="Associations"
          title="Découvrez les "
          highlight="associations de la commune"
          subtitle="Sport, culture, loisirs, solidarité, jeunesse : retrouvez rapidement une activité ou une association proche de chez vous."
        />

        <Reveal className="mx-auto mb-10 max-w-4xl">
          <div className="grid gap-3 rounded-[2rem] border border-slate-200 bg-white p-3 shadow-sm dark:border-white/10 dark:bg-slate-900 md:grid-cols-[1fr_280px]">
            <div className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-950">
              <FaSearch className="text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher une association, une discipline..."
                className="w-full bg-transparent text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400 dark:text-white"
              />
            </div>

            <div className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-950">
              <FaFilter className="text-primary" />
              <select
                value={filteredCategoryId}
                onChange={(e) => setFilteredCategoryId(e.target.value)}
                className="w-full bg-transparent text-sm font-bold text-slate-700 outline-none dark:text-white"
              >
                <option value="">Toutes les catégories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => setFilteredCategoryId("")}
              className={[
                "rounded-full px-4 py-2 text-xs font-bold transition",
                filteredCategoryId === ""
                  ? "bg-primary text-white shadow"
                  : "bg-white text-slate-600 ring-1 ring-slate-200 hover:text-primary dark:bg-slate-900 dark:text-slate-300 dark:ring-white/10",
              ].join(" ")}
            >
              Toutes
            </button>

            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => setFilteredCategoryId(String(category.id))}
                className={[
                  "rounded-full px-4 py-2 text-xs font-bold transition",
                  String(filteredCategoryId) === String(category.id)
                    ? "bg-primary text-white shadow"
                    : "bg-white text-slate-600 ring-1 ring-slate-200 hover:text-primary dark:bg-slate-900 dark:text-slate-300 dark:ring-white/10",
                ].join(" ")}
              >
                {category.label}
              </button>
            ))}
          </div>

          <p className="mt-5 text-center text-sm font-medium text-slate-500 dark:text-slate-400">
            {filteredSections.length} association
            {filteredSections.length > 1 ? "s" : ""} trouvée
            {filteredSections.length > 1 ? "s" : ""}
            {filteredSections.length > SECTIONS_PER_PAGE
              ? ` · page ${safeCurrentPage} sur ${totalPages}`
              : ""}
          </p>
        </Reveal>

        {filteredSections.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
            Aucune association trouvée.
          </div>
        ) : (
          <>
            <div
              id="sections-list"
              className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4"
            >
              {displayedSections.map((section, index) => {
                const category = categoriesById[String(section.category_id)];

                return (
                  <Reveal key={section.id || index} delay={index * 0.025}>
                    <SectionCard
                      title={section.nom_section}
                      discipline={section.discipline_section}
                      imageSrc={section.photoURL}
                      logoSrc={section.logo_section}
                      lienSection={section.lien_section || section.slug}
                      responsable={section.responsable_section}
                      adresse={section.adresse_section}
                      category={category?.label || "Non catégorisée"}
                    />
                  </Reveal>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="mt-10 flex flex-col items-center justify-center gap-4">
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => goToPage(safeCurrentPage - 1)}
                    disabled={safeCurrentPage === 1}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
                  >
                    <FaChevronLeft />
                    Précédent
                  </button>

                  {pageNumbers.map((page, index) => {
                    const previousPage = pageNumbers[index - 1];
                    const shouldShowDots = previousPage && page - previousPage > 1;

                    return (
                      <React.Fragment key={page}>
                        {shouldShowDots && (
                          <span className="px-1 text-sm font-bold text-slate-400">
                            ...
                          </span>
                        )}

                        <button
                          type="button"
                          onClick={() => goToPage(page)}
                          className={[
                            "flex h-10 w-10 items-center justify-center rounded-2xl text-sm font-bold transition",
                            safeCurrentPage === page
                              ? "bg-primary text-white shadow"
                              : "bg-white text-slate-600 ring-1 ring-slate-200 hover:text-primary dark:bg-slate-900 dark:text-slate-300 dark:ring-white/10",
                          ].join(" ")}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => goToPage(safeCurrentPage + 1)}
                    disabled={safeCurrentPage === totalPages}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
                  >
                    Suivant
                    <FaChevronRight />
                  </button>
                </div>

                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  Affichage de{" "}
                  {Math.min(
                    (safeCurrentPage - 1) * SECTIONS_PER_PAGE + 1,
                    filteredSections.length
                  )}{" "}
                  à{" "}
                  {Math.min(
                    safeCurrentPage * SECTIONS_PER_PAGE,
                    filteredSections.length
                  )}{" "}
                  sur {filteredSections.length}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default Sections;